import React from 'react';
import { X } from 'lucide-react';
import { SshProfileManager } from './terminal/SshProfileManager';
import type { SshProfile } from '../../types/ssh';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Slider } from '../ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

export interface TerminalSettings {
  backgroundOpacity: number;
  fontSize: number;
  fontFamily: string;
  textColor: string;
  cursorStyle: string;
  scrollback: number;
}

interface TerminalSettingsProps {
  settings: TerminalSettings;
  sshProfiles: SshProfile[];
  onChange: (settings: TerminalSettings) => void;
  onSaveSshProfile: (profile: SshProfile) => void;
  onDeleteSshProfile: (profileId: string) => void;
  onClose: () => void;
  isElectron: boolean;
}

export const TerminalSettingsDialog: React.FC<TerminalSettingsProps> = ({
  settings,
  sshProfiles,
  onChange,
  onSaveSshProfile,
  onDeleteSshProfile,
  onClose,
  isElectron,
}) => {
  const handleSettingChange = (key: keyof TerminalSettings, value: string | number) => {
    onChange({
      ...settings,
      [key]: value,
    });
  };

  const resetToDefaults = () => {
    onChange({
      backgroundOpacity: 100,
      fontSize: 14,
      fontFamily: 'monospace',
      textColor: 'green',
      cursorStyle: 'block',
      scrollback: 1000,
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <Card
        className="w-full max-w-4xl cursor-move border border-border bg-card shadow-xl"
        onMouseDown={(event) => {
          const card = event.currentTarget;
          const rect = card.getBoundingClientRect();
          const offsetX = event.clientX - rect.left;
          const offsetY = event.clientY - rect.top;

          const handleMouseMove = (mouseEvent: MouseEvent) => {
            card.style.position = 'fixed';
            card.style.left = `${mouseEvent.clientX - offsetX}px`;
            card.style.top = `${mouseEvent.clientY - offsetY}px`;
            card.style.transform = 'none';
          };

          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }}
      >
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Terminal Settings</CardTitle>
              <CardDescription>
                Customize terminal appearance and manage saved SSH profiles.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
              <X size={16} />
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="appearance" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="appearance">Appearance</TabsTrigger>
              <TabsTrigger value="ssh">SSH Profiles</TabsTrigger>
            </TabsList>

            <TabsContent value="appearance" className="mt-0 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="opacity">Background Opacity: {settings.backgroundOpacity}%</Label>
                <Slider
                  id="opacity"
                  min={10}
                  max={100}
                  step={5}
                  value={[settings.backgroundOpacity]}
                  onValueChange={(value) => handleSettingChange('backgroundOpacity', value[0])}
                  className="w-full"
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="fontSize">Font Size: {settings.fontSize}px</Label>
                <Slider
                  id="fontSize"
                  min={10}
                  max={24}
                  step={1}
                  value={[settings.fontSize]}
                  onValueChange={(value) => handleSettingChange('fontSize', value[0])}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fontFamily">Font Family</Label>
                <Select value={settings.fontFamily} onValueChange={(value) => handleSettingChange('fontFamily', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monospace">Monospace (Default)</SelectItem>
                    <SelectItem value="'Courier New', monospace">Courier New</SelectItem>
                    <SelectItem value="'Consolas', monospace">Consolas</SelectItem>
                    <SelectItem value="'Monaco', monospace">Monaco</SelectItem>
                    <SelectItem value="'Source Code Pro', monospace">Source Code Pro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="textColor">Text Color</Label>
                <Select value={settings.textColor} onValueChange={(value) => handleSettingChange('textColor', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="green">Green (Classic)</SelectItem>
                    <SelectItem value="white">White</SelectItem>
                    <SelectItem value="cyan">Cyan</SelectItem>
                    <SelectItem value="yellow">Yellow</SelectItem>
                    <SelectItem value="orange">Orange</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cursorStyle">Cursor Style</Label>
                <Select value={settings.cursorStyle} onValueChange={(value) => handleSettingChange('cursorStyle', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="block">Block</SelectItem>
                    <SelectItem value="underline">Underline</SelectItem>
                    <SelectItem value="bar">Bar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="scrollback">Scrollback Lines: {settings.scrollback}</Label>
                <Slider
                  id="scrollback"
                  min={100}
                  max={10000}
                  step={100}
                  value={[settings.scrollback]}
                  onValueChange={(value) => handleSettingChange('scrollback', value[0])}
                  className="w-full"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={resetToDefaults} className="flex-1">
                  Reset to Defaults
                </Button>
                <Button onClick={onClose} className="flex-1">
                  Done
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="ssh" className="mt-0">
              <SshProfileManager
                profiles={sshProfiles}
                onSaveProfile={onSaveSshProfile}
                onDeleteProfile={onDeleteSshProfile}
                isElectron={isElectron}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
